import React, { useState, useEffect } from 'react';
import useUnifiedAnalytics from '../analytics/useUnifiedAnalytics';
import { EVENTS } from '../analytics/events';
import { useExperiment } from '@statsig/react-bindings';

const CTAModal = ({ contestant, onClose, pollConfig }) => {
  const experiment = useExperiment("pilot_cta");
  const analytics = useUnifiedAnalytics();
  
  // Debug logging for Statsig experiment
  console.log('Statsig experiment:', {
    experimentName: "pilot_cta",
    experimentObject: experiment,
    experimentValues: {
      thankYouSubtitle: experiment.get("thankYouSubtitle", "DEFAULT_VALUE_NOT_SET"),
      ctaDescription: experiment.get("ctaDescription", "DEFAULT_VALUE_NOT_SET"),
      ctaImageUrl: experiment.get("ctaImageUrl", "DEFAULT_VALUE_NOT_SET")
    }
  });
  
  // Default values if pollConfig is not available
  const defaultConfig = {
    thankYouTitle: 'Thank You For Voting!',
    thankYouSubtitle: 'Want More Than Just Voting?',
    ctaText: 'Enter STA Studios Discord',
    ctaUrl: 'https://discord.gg/zxafq5vjTS',
    ctaDescription: 'Join the Discord to get early leaks, secret polls, and fan-only rewards. Don\'t miss what comes next.',
    ctaImageUrl: '/assets/confessional-thumbnail.png'
  };
  
  // Get values with proper fallback chain using the useExperiment hook
  const thankYouTitle = pollConfig?.thankYouTitle || defaultConfig.thankYouTitle;
  const thankYouSubtitle = experiment.get("thankYouSubtitle", pollConfig?.thankYouSubtitle || defaultConfig.thankYouSubtitle);
  const ctaText = pollConfig?.ctaText || defaultConfig.ctaText;
  const ctaUrl = pollConfig?.ctaUrl || defaultConfig.ctaUrl;
  const ctaDescription = experiment.get("ctaDescription", pollConfig?.ctaDescription || defaultConfig.ctaDescription);
  const ctaImageUrl = experiment.get("ctaImageUrl", pollConfig?.ctaImageUrl || defaultConfig.ctaImageUrl);
  
  // Debug logging for final values after fallback chain
  console.log('Final CTA values:', {
    thankYouTitle,
    thankYouSubtitle,
    ctaText,
    ctaUrl,
    ctaDescription,
    ctaImageUrl,
    pollConfigValues: pollConfig,
    defaultValues: defaultConfig
  });
  
  useEffect(() => {
    // Track when the modal is shown (will track in both Statsig and GA)
    analytics.trackEvent(EVENTS.CTA_MODAL_SHOWN, contestant?.id);
    // Store the time when modal was opened for duration tracking
    analytics.modalOpenTime = new Date();
    
    return () => {
      // Track when the modal is closed (will track in both Statsig and GA)
      const durationSeconds = analytics.modalOpenTime ? 
        Math.floor((new Date() - analytics.modalOpenTime) / 1000) : 0;
      
      analytics.trackEvent(EVENTS.CTA_MODAL_CLOSED, null, {
        modal_duration_seconds: durationSeconds
      });
    };
  }, [analytics, contestant]);
  
  // Handle CTA button click
  const handleCTAClick = () => {
    // Track with both Statsig and Google Analytics
    analytics.trackEvent(EVENTS.CTA_BUTTON_CLICKED, 'discord_join', {
      contestant_id: contestant?.id
    });
  };
  
  // Handle close button click
  const handleCloseClick = () => {
    // Track with both Statsig and Google Analytics
    analytics.trackEvent(EVENTS.CTA_CLOSE_BUTTON_CLICKED, null, {
      contestant_id: contestant?.id,
      modal_duration_seconds: analytics.modalOpenTime ? 
        Math.floor((new Date() - analytics.modalOpenTime) / 1000) : 0
    });
    onClose();
  };
  
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-toon-yellow bg-opacity-95 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-lg">
        {/* Close button */}
        <button 
          onClick={handleCloseClick}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header */}
        <img 
          src="/assets/Toon_House_Logo.svg" 
          alt="Toon House Logo" 
          className="h-16 mx-auto mb-4"
        />
        <h2 className="text-3xl font-bold mb-2">{thankYouTitle}</h2>
        <p className="text-xl mb-6">{thankYouSubtitle}</p>
        
        {/* Thumbnail */}
        <div className="mb-6 rounded-lg overflow-hidden border-4 border-toon-yellow shadow-lg">
          <img 
            src={ctaImageUrl} 
            alt="Exclusive Content" 
            className="w-full h-auto object-contain"
            style={{ maxHeight: '200px' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/assets/confessional-thumbnail.png';
            }}
          />
        </div>
        
        <div className="flex flex-col space-y-4">
          <a 
            href={ctaUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={handleCTAClick}
            className="w-full bg-indigo-600 text-white py-3 px-6 rounded-md font-bold text-center hover:bg-indigo-700 transition-colors"
          >
            {ctaText}
          </a>
        </div>
        
        {/* Explanation */}
        <p className="text-gray-600 mt-4">
          {ctaDescription}
        </p>
      </div>
    </div>
  );
};

export default CTAModal;
